import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const GST_BULK_API = "/api/v1/sales-invoices/gst-bulk";

const fmt2 = (value) =>
  value === null || value === undefined || value === "" ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const danger = { color: "var(--color-danger, #b42318)" };

const Modal = ({ title, onClose, children, actions, maxWidth = "820px" }) => (
  <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className="modal-box" style={{ maxWidth }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold">{title}</h3>
        <button className="close-btn" onClick={onClose}>
          Close
        </button>
      </div>
      {children}
      <div className="modal-actions">{actions}</div>
    </div>
  </div>
);

/** Per-invoice outcome of a bulk call: invoice number, OK / failed, message. */
const ResultTable = ({ results, extraColumns = [] }) => (
  <div className="table-wrapper">
    <table>
      <thead>
        <tr>
          <th>Invoice #</th>
          <th>Result</th>
          {extraColumns.map((column) => (
            <th key={column.label}>{column.label}</th>
          ))}
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {results.map((result) => (
          <tr key={result.invoiceId}>
            <td>{result.invoiceNumber || result.invoiceId}</td>
            <td>
              <span className={`badge ${result.success ? "badge-green" : "badge-red"}`}>{result.success ? "OK" : "Failed"}</span>
            </td>
            {extraColumns.map((column) => (
              <td key={column.label}>{column.value(result)}</td>
            ))}
            <td style={{ fontSize: "12px", maxWidth: "360px", ...(result.success ? {} : danger) }}>{result.message || "-"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Vehicle number, approximate distance and transport mode, set on all the selected invoices for their e-way bills. */
const VehicleFields = ({ value, onChange }) => (
  <div className="form-grid">
    <div className="form-group">
      <label>Vehicle Number</label>
      <input value={value.vehicleNumber} onChange={(e) => onChange({ ...value, vehicleNumber: e.target.value.toUpperCase() })} placeholder="e.g. KA01AB1234" />
    </div>
    <div className="form-group">
      <label>Approx. Distance (km)</label>
      <input
        type="number"
        min="0"
        max="4000"
        value={value.distanceKm}
        onChange={(e) => onChange({ ...value, distanceKm: e.target.value })}
        placeholder="0 = portal computes it"
      />
    </div>
    <div className="form-group">
      <label>Transport Mode</label>
      <select value={value.transportMode} onChange={(e) => onChange({ ...value, transportMode: e.target.value })}>
        <option value="1">Road</option>
        <option value="2">Rail</option>
        <option value="3">Air</option>
        <option value="4">Ship</option>
      </select>
    </div>
  </div>
);

const emptyVehicle = { vehicleNumber: "", distanceKm: "", transportMode: "1" };

const saveVehicle = (invoiceIds, vehicle) =>
  api.put(`${GST_BULK_API}/vehicle`, {
    invoiceIds,
    vehicleNumber: vehicle.vehicleNumber.trim() || null,
    distanceKm: vehicle.distanceKm === "" ? null : Number(vehicle.distanceKm),
    transportMode: vehicle.transportMode || null,
  });

/** Bulk update of the vehicle number / approximate distance of the selected invoices. */
export const BulkVehicleModal = ({ invoices, onClose, onDone }) => {
  const [vehicle, setVehicle] = useState(emptyVehicle);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);

  const save = async () => {
    if (!vehicle.vehicleNumber.trim() && vehicle.distanceKm === "") {
      toast.error("Enter the vehicle number and / or the distance");
      return;
    }
    setSaving(true);
    try {
      const outcome = await saveVehicle(
        invoices.map((invoice) => invoice.id),
        vehicle
      );
      setResults(Array.isArray(outcome) ? outcome : []);
      const failed = (outcome || []).filter((r) => !r.success).length;
      if (failed) toast.warn(`${failed} invoice(s) could not be updated`);
      else toast.success("Vehicle details updated");
      onDone?.();
    } catch (error) {
      toast.error(error.message || "Failed to update vehicle details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Vehicle / Distance — ${invoices.length} invoice(s)`}
      onClose={onClose}
      actions={
        results ? (
          <button type="button" className="primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="primary" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Update Invoices"}
            </button>
          </>
        )
      }
    >
      {results ? (
        <ResultTable results={results} />
      ) : (
        <>
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            Used for the e-way bills of the selected invoices. A blank field leaves that detail as it is on each invoice; invoices whose e-way
            bill is already generated are not changed.
          </p>
          <VehicleFields value={vehicle} onChange={setVehicle} />
        </>
      )}
    </Modal>
  );
};

/**
 * Before bulk generation: every check the e-invoice / e-way bill would be refused for, per selected invoice, shown before anything is
 * sent. Vehicle number and distance can be filled in here for the invoices missing them; then only the invoices that pass are generated.
 */
export const GstPrecheckModal = ({ invoices, onClose, onGenerated, onChanged }) => {
  const [checks, setChecks] = useState(null);
  const [checking, setChecking] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [vehicle, setVehicle] = useState(emptyVehicle);
  const [savingVehicle, setSavingVehicle] = useState(false);

  const runChecks = async () => {
    setChecking(true);
    try {
      const result = await api.post(`${GST_BULK_API}/validate`, { invoiceIds: invoices.map((invoice) => invoice.id) });
      setChecks(Array.isArray(result) ? result : []);
    } catch (error) {
      toast.error(error.message || "Could not validate the invoices");
      setChecks([]);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    runChecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ready = (checks || []).filter((check) => check.ready && (check.eInvoiceNeeded || check.eWayBillNeeded));
  const failing = (checks || []).filter((check) => !check.ready);
  const needVehicle = failing.filter((check) => check.eWayBillNeeded);

  const applyVehicle = async () => {
    if (!vehicle.vehicleNumber.trim() && vehicle.distanceKm === "") {
      toast.error("Enter the vehicle number and / or the distance");
      return;
    }
    setSavingVehicle(true);
    try {
      const outcome = await saveVehicle(
        needVehicle.map((check) => check.invoiceId),
        vehicle
      );
      const failed = (outcome || []).filter((r) => !r.success);
      if (failed.length) toast.warn(failed.map((r) => `${r.invoiceNumber || r.invoiceId}: ${r.message}`).join("; "));
      else toast.success("Vehicle details saved");
      onChanged?.();
      await runChecks();
    } catch (error) {
      toast.error(error.message || "Failed to update vehicle details");
    } finally {
      setSavingVehicle(false);
    }
  };

  const generate = async () => {
    if (ready.length === 0) return;
    setGenerating(true);
    try {
      const results = await api.post(`${GST_BULK_API}/generate`, { invoiceIds: ready.map((check) => check.invoiceId) });
      onGenerated(Array.isArray(results) ? results : []);
    } catch (error) {
      toast.error(error.message || "Failed to generate e-invoices / e-way bills");
    } finally {
      setGenerating(false);
    }
  };

  const needs = (check) => [check.eInvoiceNeeded ? "E-invoice" : null, check.eWayBillNeeded ? "E-way bill" : null].filter(Boolean).join(" + ") || "Nothing";

  return (
    <Modal
      title="Check before generating E-Invoice / E-Way Bill"
      onClose={onClose}
      maxWidth="960px"
      actions={
        <>
          <button type="button" className="secondary" onClick={runChecks} disabled={checking}>
            {checking ? "Checking..." : "Check again"}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="primary" onClick={generate} disabled={generating || checking || ready.length === 0}>
            {generating ? "Generating..." : `Generate for ${ready.length} ready invoice(s)`}
          </button>
        </>
      }
    >
      {checks === null ? (
        <p className="page-subtitle">Validating the selected invoices...</p>
      ) : (
        <>
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            {ready.length} ready, {failing.length} with problems. Nothing is sent to the GST portal for an invoice with problems — fix them
            (customer / product master, or the vehicle details below) and check again.
          </p>
          {needVehicle.length > 0 ? (
            <div style={{ border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
              <strong style={{ fontSize: 13 }}>Vehicle / distance for the {needVehicle.length} e-way bill invoice(s) with problems</strong>
              <VehicleFields value={vehicle} onChange={setVehicle} />
              <button type="button" className="secondary" onClick={applyVehicle} disabled={savingVehicle}>
                {savingVehicle ? "Saving..." : "Save on these invoices"}
              </button>
            </div>
          ) : null}
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Needs</th>
                  <th>Status</th>
                  <th>Problems</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((check) => (
                  <tr key={check.invoiceId}>
                    <td>{check.invoiceNumber || check.invoiceId}</td>
                    <td>{needs(check)}</td>
                    <td>
                      <span className={`badge ${check.ready ? "badge-green" : "badge-red"}`}>{check.ready ? "Ready" : "Errors"}</span>
                    </td>
                    <td style={{ fontSize: "12px", maxWidth: "460px", ...danger }}>
                      {check.errors?.length ? (
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          {check.errors.map((error) => (
                            <li key={error}>{error}</li>
                          ))}
                        </ul>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
};

/**
 * Full-amount credit notes for the selected invoices (e.g. bills raised on one customer but delivered to another): each invoice is fully
 * cancelled (or returned) and credited, as the single-invoice action does.
 */
export const BulkCreditNoteModal = ({ invoices, onClose, onDone }) => {
  const [mode, setMode] = useState("CANCEL");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);
  const eligible = invoices.filter((invoice) => invoice.status !== "CANCELLED" && invoice.status !== "RETURNED");
  const total = eligible.reduce((sum, invoice) => sum + Number(invoice.totalAmount || 0), 0);

  const submit = async () => {
    if (eligible.length === 0) return;
    if (!window.confirm(`Create full credit notes for ${eligible.length} invoice(s) totalling ₹${fmt2(total)}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const outcome = await api.post("/api/v1/sales-invoices/bulk-credit-notes", {
        invoiceIds: eligible.map((invoice) => invoice.id),
        mode,
        remarks: remarks.trim() || null,
      });
      setResults(Array.isArray(outcome) ? outcome : []);
      const failed = (outcome || []).filter((r) => !r.success).length;
      if (failed) toast.warn(`${failed} invoice(s) could not be credited`);
      else toast.success("Credit notes created");
      onDone?.();
    } catch (error) {
      toast.error(error.message || "Failed to create credit notes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Bulk Credit Note — ${invoices.length} invoice(s)`}
      onClose={onClose}
      actions={
        results ? (
          <button type="button" className="primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="primary" onClick={submit} disabled={saving || eligible.length === 0}>
              {saving ? "Creating..." : `Create ${eligible.length} Credit Note(s)`}
            </button>
          </>
        )
      }
    >
      {results ? (
        <ResultTable
          results={results}
          extraColumns={[
            { label: "Credit Note", value: (r) => r.creditNoteNumber || "-" },
            { label: "Amount", value: (r) => (r.creditNoteAmount == null ? "-" : fmt2(r.creditNoteAmount)) },
          ]}
        />
      ) : (
        <>
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            Each invoice is credited in full: its status becomes cancelled (or returned), a credit note is raised for everything still on it,
            scheme qualification is reversed and any live e-way bill / e-invoice is cancelled.
            {eligible.length < invoices.length ? ` ${invoices.length - eligible.length} already cancelled / returned invoice(s) are skipped.` : ""}
          </p>
          <div className="form-grid">
            <div className="form-group">
              <label>Type</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="CANCEL">Full cancellation</option>
                <option value="RETURN">Full return (goods back in stock)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Remarks</label>
              <input value={remarks} maxLength={500} onChange={(e) => setRemarks(e.target.value)} placeholder="e.g. Billed to wrong customer" />
            </div>
          </div>
          <div className="table-wrapper" style={{ maxHeight: 300, overflowY: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoiceNumber}</td>
                    <td>{invoice.customerName || invoice.customerId}</td>
                    <td>{invoice.invoiceDate}</td>
                    <td style={{ textAlign: "right" }}>{fmt2(invoice.totalAmount)}</td>
                    <td>{invoice.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ marginTop: 8, fontWeight: 600 }}>Total to credit: ₹{fmt2(total)}</p>
        </>
      )}
    </Modal>
  );
};
